import React, { useEffect, useId, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { fetchSellerSuggestions } from '../../../util/api';

import {
  SELLER_PARAM,
  avatarInitial,
  avatarTintForId,
  sellerDisplayName,
  splitMatchedText,
  normalizeSellerParam,
} from './sellersFilterHelpers';

import css from './SellerUsernameCombobox.module.css';

const DEBOUNCE_MS = 280;
const MIN_CHARS = 2;

const SellerAvatar = ({ seller, label, className }) => {
  const tint = avatarTintForId(seller?.id || label);
  const initial = avatarInitial(label);
  const style = {
    backgroundColor: tint,
    ...(seller?.avatarUrl ? { backgroundImage: `url(${seller.avatarUrl})` } : {}),
  };
  return (
    <span className={className} style={style} aria-hidden="true">
      {seller?.avatarUrl ? null : initial}
    </span>
  );
};

const HighlightedName = ({ text, query }) => {
  const parts = splitMatchedText(text, query);
  return (
    <>
      {parts.map((part, index) =>
        part.hit ? (
          <b key={index} className={css.hit}>
            {part.text}
          </b>
        ) : (
          <span key={index}>{part.text}</span>
        )
      )}
    </>
  );
};

/**
 * Typeahead that resolves to a single `?seller=` forum username.
 *
 * @param {Object} props
 * @param {string} props.id
 * @param {Object} props.urlQueryParams
 * @param {Object|null} [props.activeSeller]
 * @param {boolean} [props.sellerNotFound]
 * @param {Function} props.onSubmit merge-style URL param updater
 */
const SellerUsernameCombobox = props => {
  const intl = useIntl();
  const { id, urlQueryParams = {}, activeSeller = null, sellerNotFound = false, onSubmit } = props;

  const listboxId = useId();
  const liveRegionId = useId();
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const urlUsername = normalizeSellerParam(urlQueryParams[SELLER_PARAM]);
  const lockedSeller =
    !sellerNotFound &&
    activeSeller &&
    (!urlUsername ||
      (activeSeller.username && activeSeller.username.toLowerCase() === urlUsername.toLowerCase()))
      ? activeSeller
      : null;

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [results, setResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = event => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const runSuggest = value => {
    const trimmed = value.trim();
    if (trimmed.length < MIN_CHARS) {
      setResults([]);
      setPending(false);
      setOpen(false);
      setStatusMessage('');
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setPending(true);
    setOpen(true);
    setStatusMessage(intl.formatMessage({ id: 'SellersFilter.searching' }));

    fetchSellerSuggestions(trimmed)
      .then(payload => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        const sellers = Array.isArray(payload?.sellers) ? payload.sellers : [];
        setResults(sellers);
        setPending(false);
        setActiveIndex(sellers.length > 0 ? 0 : -1);
        setStatusMessage(
          sellers.length > 0
            ? intl.formatMessage({ id: 'SellersFilter.foundCount' }, { count: sellers.length })
            : intl.formatMessage({ id: 'SellersFilter.noMatches' }, { query: trimmed })
        );
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setResults([]);
        setPending(false);
        setStatusMessage(intl.formatMessage({ id: 'SellersFilter.noMatches' }, { query: trimmed }));
      });
  };

  const handleQueryChange = value => {
    setQuery(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    requestIdRef.current += 1;
    const trimmed = value.trim();
    if (trimmed.length < MIN_CHARS) {
      setPending(false);
      setResults([]);
      setOpen(false);
      setStatusMessage('');
      return;
    }
    setPending(true);
    setOpen(true);
    debounceRef.current = setTimeout(() => runSuggest(value), DEBOUNCE_MS);
  };

  const pickSeller = seller => {
    const username = typeof seller?.username === 'string' ? seller.username.trim() : '';
    if (!username) {
      return;
    }
    setQuery('');
    setOpen(false);
    setResults([]);
    onSubmit({ [SELLER_PARAM]: username });
  };

  const clearSeller = () => {
    setQuery('');
    setOpen(false);
    setResults([]);
    onSubmit({ [SELLER_PARAM]: null });
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = event => {
    if (event.key === 'ArrowDown' && results.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
      setActiveIndex(index => (index + 1) % results.length);
      return;
    }
    if (event.key === 'ArrowUp' && results.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
      setActiveIndex(index => (index <= 0 ? results.length - 1 : index - 1));
      return;
    }
    if (event.key === 'Enter' && open && activeIndex >= 0 && results[activeIndex]) {
      event.preventDefault();
      event.stopPropagation();
      pickSeller(results[activeIndex]);
      return;
    }
    if (event.key === 'Escape') {
      if (open) {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        return;
      }
      if (query) {
        event.preventDefault();
        event.stopPropagation();
        handleQueryChange('');
      }
    }
  };

  if (lockedSeller) {
    const label = sellerDisplayName(lockedSeller);
    const count = Number.isFinite(lockedSeller.listingCount) ? lockedSeller.listingCount : null;
    return (
      <div className={css.root} ref={rootRef}>
        <div className={css.card}>
          <div className={css.cardTop}>
            <SellerAvatar seller={lockedSeller} label={label} className={css.cardAvatar} />
            <span className={css.cardBody}>
              <span className={css.cardName}>{label}</span>
              {count != null ? (
                <span className={css.sub}>
                  {intl.formatMessage({ id: 'SellersFilter.listingCount' }, { count })}
                </span>
              ) : null}
            </span>
            <button
              type="button"
              className={css.cardClear}
              onClick={clearSeller}
              aria-label={intl.formatMessage({ id: 'SellersFilter.clearSeller' })}
            >
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  const showDropdown = open && query.trim().length >= MIN_CHARS;
  const fieldActive = query.length > 0;

  return (
    <div className={css.root} ref={rootRef}>
      <div className={classNames(css.field, { [css.fieldActive]: fieldActive })}>
        <span className={css.at} aria-hidden="true">
          @
        </span>
        <input
          id={id}
          ref={inputRef}
          className={css.input}
          value={query}
          onChange={event => handleQueryChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={intl.formatMessage({ id: 'SellersFilter.usernamePlaceholder' })}
          autoComplete="off"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={showDropdown ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={
            showDropdown && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined
          }
          aria-describedby={liveRegionId}
        />
        {pending ? <span className={css.spinner} aria-hidden="true" /> : null}
        {!pending && query ? (
          <button
            type="button"
            className={css.fieldClear}
            onClick={() => handleQueryChange('')}
            aria-label={intl.formatMessage({ id: 'SellersFilter.clearQuery' })}
          >
            ×
          </button>
        ) : null}
      </div>
      {showDropdown ? (
        <div className={css.dropdown} id={listboxId} role="listbox">
          {pending && results.length === 0 ? (
            <div className={css.status}>
              {intl.formatMessage({ id: 'SellersFilter.searching' })}
            </div>
          ) : null}
          {!pending && results.length === 0 ? (
            <div className={css.empty}>
              {intl.formatMessage({ id: 'SellersFilter.noMatches' }, { query: query.trim() })}
            </div>
          ) : null}
          {results.map((seller, index) => {
            const label = sellerDisplayName(seller);
            return (
              <div
                key={seller.username || seller.id || label}
                id={`${id}-option-${index}`}
                role="option"
                tabIndex={-1}
                aria-label={label}
                aria-selected={index === activeIndex}
                className={classNames(css.option, { [css.optionActive]: index === activeIndex })}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={event => event.preventDefault()}
                onClick={() => pickSeller(seller)}
              >
                <SellerAvatar seller={seller} label={label} className={css.avatar} />
                <span className={css.optionBody}>
                  <span className={css.username}>
                    <HighlightedName text={label} query={query} />
                  </span>
                  <span className={css.sub}>
                    {intl.formatMessage(
                      { id: 'SellersFilter.listingCount' },
                      { count: seller.listingCount || 0 }
                    )}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
      <div id={liveRegionId} className={css.visuallyHidden} role="status" aria-live="polite">
        {statusMessage}
      </div>
    </div>
  );
};

export default SellerUsernameCombobox;
